import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { CalendarDays, Check, Plus, Search, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { localDateString, subjectTint, SUBJECTS, type Subject } from "@/lib/app-types";
import { useInputKeyboardScroll } from "@/hooks/use-input-keyboard-scroll";
import { SYLLABUS_GROUPS } from "@/lib/syllabus";
import { fetchChapters } from "@/lib/chapters.functions";
import { daysBetween, type TestChapterInput } from "@/lib/test-plan";
import { addDays } from "@/lib/lecture-plan";
import { BottomSheet } from "./bottom-sheet";
import { NeuralButton } from "./neural-button";

type Picked = TestChapterInput & { key: string };

const keyOf = (subject: string, chapter: string) => `${subject}::${chapter.toLowerCase()}`;

export function AddTestSheet({
  open,
  busy,
  mode = "test",
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy?: boolean;
  /** "chapters" reuses the picker to append chapters to an existing test. */
  mode?: "test" | "chapters";
  onClose: () => void;
  onSubmit: (input: { name: string; testDate: string; chapters: TestChapterInput[] }) => void;
}) {
  const today = localDateString();
  const [name, setName] = useState("");
  const [testDate, setTestDate] = useState(addDays(today, 7));
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Picked[]>([]);
  const [subject, setSubject] = useState<Subject | null>(null);
  const [klass, setKlass] = useState<11 | 12 | null>(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customSubject, setCustomSubject] = useState<Subject>("Physics");
  const [customName, setCustomName] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const nameScroll = useInputKeyboardScroll<HTMLInputElement>();
  const { data: savedChapters = [] } = useQuery({
    queryKey: ["chapters"],
    queryFn: () => fetchChapters(),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (open) {
      setName("");
      setTestDate(addDays(localDateString(), 7));
      setQuery("");
      setPicked([]);
      setCustomOpen(false);
      setCustomName("");
      setSubject(null);
      setKlass(null);
    }
  }, [open]);

  // Native-feeling sheet: focus the first field once the slide-up settles.
  useEffect(() => {
    if (!open || mode === "chapters") return;
    const t = window.setTimeout(() => nameRef.current?.focus(), 380);
    return () => window.clearTimeout(t);
  }, [open, mode]);

  /** Show only the built-in chapters for the active subject and class. */
  const groups = useMemo(() => {
    if (!subject || !klass) return [];

    const classPrefix = `Class ${klass} ·`;
    const builtIn = SYLLABUS_GROUPS.filter(
      (group) => group.subject === subject && group.label.startsWith(classPrefix),
    ).map((group) => ({
      label:
        subject === "Chemistry"
          ? `${group.label.split("— ")[1] ?? "Chemistry"} Chemistry`
          : group.label.split("· ")[1] ?? subject,
      rows: group.chapters.map((chapter) => ({ subject: group.subject, chapter })),
    }));

    const builtInKeys = new Set(
      builtIn.flatMap((group) => group.rows.map((row) => keyOf(row.subject, row.chapter))),
    );
    const savedRows = savedChapters
      .filter((chapter) => chapter.subject === subject)
      .filter((chapter) => !builtInKeys.has(keyOf(chapter.subject, chapter.chapter)))
      .map((chapter) => ({ subject: chapter.subject, chapter: chapter.chapter }));
    const all = savedRows.length
      ? [{ label: "Your chapters", rows: savedRows }, ...builtIn]
      : builtIn;

    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all
      .map((g) => ({
        label: g.label,
        rows: g.rows.filter(
          (c) =>
            c.chapter.toLowerCase().includes(q) ||
            c.subject.toLowerCase().includes(q) ||
            g.label.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.rows.length > 0);
  }, [klass, query, savedChapters, subject]);

  const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const daysLeft = Math.max(0, daysBetween(today, testDate));
  const pickedKeys = new Set(picked.map((p) => p.key));

  const toggle = (subject: string, chapter: string) => {
    const key = keyOf(subject, chapter);
    setPicked((prev) =>
      prev.some((p) => p.key === key)
        ? prev.filter((p) => p.key !== key)
        : [...prev, { key, subject, chapter, weak: false }],
    );
  };

  const setWeak = (key: string, weak: boolean) =>
    setPicked((prev) => prev.map((p) => (p.key === key ? { ...p, weak } : p)));

  const addCustom = () => {
    const chapter = customName.trim();
    if (!chapter) return;
    const key = keyOf(customSubject, chapter);
    if (!pickedKeys.has(key)) {
      setPicked((prev) => [...prev, { key, subject: customSubject, chapter, weak: false }]);
    }
    setCustomName("");
    setCustomOpen(false);
  };

  const chaptersOnly = mode === "chapters";
  const canSubmit = chaptersOnly
    ? picked.length > 0 && !busy
    : name.trim().length > 0 && picked.length > 0 && daysLeft >= 1 && !busy;

  return (
    <BottomSheet
      open={open}
      tall
      onClose={onClose}
      title={chaptersOnly ? "Add chapters" : "Add test"}
      subtitle={
        chaptersOnly
          ? "Pick what to add — the remaining days rebalance automatically."
          : "Name it, set the date and pick the chapters you need ready."
      }
      footer={
        <NeuralButton
          disabled={!canSubmit}
          onClick={() =>
            onSubmit({
              name: name.trim(),
              testDate,
              chapters: picked.map(({ subject, chapter, weak }) => ({ subject, chapter, weak })),
            })
          }
          className="w-full"
        >
          <Sparkles className="size-4" />
          {busy
            ? chaptersOnly
              ? "Rebalancing…"
              : "Generating…"
            : chaptersOnly
              ? "Add & Rebalance"
              : "Generate My Schedule"}
        </NeuralButton>
      }
    >
      <div className="space-y-5">
        {chaptersOnly ? null : (
        <>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Test name
          </p>
          <input
            ref={(el) => {
              nameRef.current = el;
              nameScroll.ref.current = el;
            }}
            onFocus={nameScroll.onFocus}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Weekly Test 04"
            className="mt-2.5 h-12 w-full rounded-full border border-hairline bg-surface px-5 text-[14px] font-medium outline-none placeholder:text-muted-foreground/70 focus:border-primary/40"
          />
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Test date
          </p>
          <div className="mt-2.5 flex items-center gap-2.5">
            <label className="flex h-12 flex-1 items-center gap-3 rounded-full border border-hairline bg-surface px-5">
              <CalendarDays className="size-4 shrink-0 text-primary" />
              <input
                type="date"
                value={testDate}
                min={addDays(today, 1)}
                onChange={(e) => e.target.value && setTestDate(e.target.value)}
                className="w-full bg-transparent text-[14px] font-medium outline-none dark:[color-scheme:dark]"
              />
            </label>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={daysLeft}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ type: "spring", stiffness: 380, damping: 26 }}
                className="shrink-0 rounded-full border border-hairline bg-surface px-4 py-3 text-[13px] font-bold"
              >
                <span className="neural-text">{daysLeft}</span>{" "}
                <span className="text-muted-foreground">
                  {daysLeft === 1 ? "day left" : "days left"}
                </span>
              </motion.span>
            </AnimatePresence>
          </div>
        </div>
        </>
        )}

        <div>
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Chapters ({picked.length})
            </p>
            <button
              type="button"
              onClick={() => setCustomOpen((v) => !v)}
              className="flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 text-[11px] font-semibold text-primary"
            >
              <Plus className="size-3.5" /> Add custom chapter
            </button>
          </div>

          <AnimatePresence initial={false}>
            {customOpen ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 30 }}
                className="overflow-hidden"
              >
                <div className="mt-3 space-y-2.5 rounded-3xl border border-hairline bg-surface p-3">
                  <div className="grid grid-cols-5 gap-2">
                    {SUBJECTS.map((item) => (
                      <button
                        key={item}
                        type="button"
                        onClick={() => setCustomSubject(item)}
                        className={cn(
                          "rounded-full px-2 py-2 text-[11px] font-semibold transition-colors",
                          customSubject === item
                            ? "[background-image:var(--gradient-brand)] text-primary-foreground"
                            : "border border-hairline bg-surface text-muted-foreground",
                        )}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder="Chapter name"
                      className="h-11 flex-1 rounded-full border border-hairline bg-surface px-4 text-[13px] outline-none placeholder:text-muted-foreground/70"
                    />
                    <motion.button
                      type="button"
                      whileTap={{ scale: 0.95 }}
                      onClick={addCustom}
                      className="h-11 rounded-full [background-image:var(--gradient-brand)] px-5 text-[13px] font-semibold text-primary-foreground"
                    >
                      Add
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>

          <div className="mt-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Select subject
            </p>
            <div className="mt-2 grid grid-cols-5 gap-2">
              {SUBJECTS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setSubject(item);
                    setKlass(null);
                    setQuery("");
                  }}
                  className={cn(
                    "rounded-full px-2 py-2 text-[11px] font-semibold transition-colors",
                    subject === item
                      ? "[background-image:var(--gradient-brand)] text-primary-foreground"
                      : "border border-hairline bg-surface text-muted-foreground",
                  )}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          {subject ? (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Select class
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {([11, 12] as const).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => {
                      setKlass(item);
                      setQuery("");
                    }}
                    className={cn(
                      "rounded-full px-3 py-2 text-[11px] font-semibold transition-colors",
                      klass === item
                        ? "[background-image:var(--gradient-brand)] text-primary-foreground"
                        : "border border-hairline bg-surface text-muted-foreground",
                    )}
                  >
                    Class {item}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {subject && klass ? (
            <label className="mt-3 flex h-11 items-center gap-3 rounded-full border border-hairline bg-surface px-4">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your syllabus"
                className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
              />
            </label>
          ) : null}

          <div className="mt-3 max-h-56 space-y-2 overflow-y-auto pr-1">
            {subject && klass && totalRows === 0 ? (
              <p className="rounded-2xl border border-hairline bg-surface px-4 py-3 text-[12px] text-muted-foreground">
                No chapters match — use “Add custom chapter”.
              </p>
            ) : null}
            {groups.map((group) => (
              <div key={group.label} className="space-y-2">
                <p className="px-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                {group.rows.map((c) => {
                  const key = keyOf(c.subject, c.chapter);
                  const active = pickedKeys.has(key);
                  return (
                    <motion.button
                      key={`${group.label}::${key}`}
                      type="button"
                      whileTap={{ scale: 0.98 }}
                      onClick={() => toggle(c.subject, c.chapter)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-full border px-4 py-2.5 text-left transition-colors",
                        active
                          ? "border-primary/40 bg-primary/12"
                          : "border-hairline bg-surface",
                      )}
                    >
                      <span
                        className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded-full border",
                          active ? "border-primary bg-primary" : "border-hairline",
                        )}
                      >
                        {active ? <Check className="size-3.5 text-primary-foreground" /> : null}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn("block text-[10px] font-semibold uppercase tracking-wider", subjectTint(c.subject))}>
                          {c.subject}
                        </span>
                        <span className="block truncate text-[13px] font-semibold">{c.chapter}</span>
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {picked.length ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Confidence
            </p>
            <div className="mt-2.5 space-y-2">
              {picked.map((p) => (
                <div
                  key={p.key}
                  className="flex items-center gap-3 rounded-full border border-hairline bg-surface px-4 py-2"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">
                    {p.chapter}
                  </span>
                  <div className="flex shrink-0 gap-1 rounded-full bg-surface p-1">
                    {(["weak", "strong"] as const).map((mode) => {
                      const active = (mode === "weak") === p.weak;
                      return (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => setWeak(p.key, mode === "weak")}
                          className={cn(
                            "rounded-full px-3 py-1 text-[11px] font-semibold capitalize transition-colors",
                            active
                              ? mode === "weak"
                                ? "bg-warning/20 text-warning"
                                : "bg-success/20 text-success"
                              : "text-muted-foreground",
                          )}
                        >
                          {mode}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </BottomSheet>
  );
}
